import React from 'react'
import { observer } from 'mobx-react-lite'
import { addMonths } from 'date-fns/addMonths'
import { startOfMonth } from 'date-fns/startOfMonth'
import { endOfMonth } from 'date-fns/endOfMonth'
import { subMonths } from 'date-fns/subMonths'
import { isBefore } from 'date-fns/isBefore'

import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { ChevronLeft, ChevronRight } from '@mxenabled/mx-icons'

import { H2, Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  useEvent,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
  useScreenSize,
  useWidgetContainerProvider,
} from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { type Recurrence } from '../../core'

import { RecurrenceCalendar } from './recurrencecalendar/RecurrenceCalendar'
import { RecurrenceList } from './RecurrenceList'
import { RecurringStatus } from './RecurringStatus'
import { RecurringTransactionDetailsDrawer } from './RecurringTransactionDetailsDrawer'
import { buildDynamicCopy } from '../../core/utils/Localization'

export const RecurringTransactions = observer(() => {
  const { recurring: copy } = useGlobalCopyStore()
  const { availableWidth } = useWidgetContainerProvider()
  const { isDesktop, isSmallMobile, isMobile } = useScreenSize(availableWidth)

  const { onEvent } = useEvent()
  const {
    currentMonthExpenses,
    currentMonthIncome,
    dateRange,
    expenseTotal,
    incomeTotal,
    missedExpenseTotal,
    pastRecurrences,
    paidExpenseTotal,
    paidIncomeTotal,
    selectedDay,
    selectedDayRecurrences,
    setDateRange,
    setSelectedRecurrence,
    setSelectedRepeatingTransactionGuid,
    upcomingExpenseTotal,
    upcomingIncomeTotal,
    upcomingRecurrences,
  } = useRecurringTransactionsStore()

  const [selectedTab, setSelectedTab] = React.useState(0)

  const isPreviousMonthDisabled = isBefore(dateRange.start, new Date())

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  const onUpcomingRecurrenceClick = (recurrence: Recurrence) => {
    setSelectedRepeatingTransactionGuid(recurrence.repeatingTransactionGuid)
    setSelectedRecurrence(recurrence)
    onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_UPCOMING, {
      transaction_guid: recurrence.transaction?.guid,
    })
  }

  const onPaidRecurrenceClick = (recurrence: Recurrence) => {
    setSelectedRepeatingTransactionGuid(recurrence.repeatingTransactionGuid)
    setSelectedRecurrence(recurrence)
    onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_PAID, {
      transaction_guid: recurrence.transaction?.guid,
    })
  }

  const onNextMonthClick = () => {
    const nextMonth = addMonths(dateRange.start, 1)
    setDateRange({
      start: startOfMonth(nextMonth),
      end: endOfMonth(nextMonth),
    })
  }

  const onPreviousMonthClick = () => {
    const lastMonth = subMonths(dateRange.start, 1)
    setDateRange({
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth),
    })
  }

  return (
    <Grid
      container={true}
      sx={{
        pt: 16,
      }}
    >
      <Grid size={{ sm: 12, md: 'grow' }} width={'100%'}>
        <Stack sx={{ gap: 16, px: isDesktop ? 48 : 24 }}>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <H2 variant="h3">
              {
                buildDynamicCopy(
                  copy.header_title,
                  formatDate(dateRange.start, DATE_FORMATS_INTL.MONTH_LONG),
                ) as string
              }
            </H2>
            {!isDesktop && (
              <Stack direction="row" gap={4}>
                <IconButton
                  color="primary"
                  disabled={isPreviousMonthDisabled}
                  onClick={onPreviousMonthClick}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton color="primary" onClick={onNextMonthClick}>
                  <ChevronRight />
                </IconButton>
              </Stack>
            )}
          </Stack>

          <RecurringStatus
            expected={expenseTotal}
            missed={missedExpenseTotal}
            paid={paidExpenseTotal}
            title={copy.expenses}
            total={currentMonthExpenses.length}
            upcoming={upcomingExpenseTotal}
          />

          <RecurringStatus
            expected={incomeTotal}
            isExpense={false}
            paid={paidIncomeTotal}
            title={copy.deposits}
            total={currentMonthIncome.length}
            upcoming={upcomingIncomeTotal}
          />

          {isDesktop && (
            <RecurrenceList
              onRecurrenceClick={onUpcomingRecurrenceClick}
              recurrences={upcomingRecurrences}
              title={copy.upcoming_activity}
            />
          )}
        </Stack>
        {!isDesktop && (
          <Stack sx={{ px: isMobile ? 0 : 24 }}>
            <Tabs onChange={handleTabChange} value={selectedTab} variant="fullWidth">
              <Tab
                label={
                  <Text bold={true} sx={{ textTransform: 'none' }} variant="body1">
                    {isSmallMobile ? copy.upcoming : copy.upcoming_activity}
                  </Text>
                }
              />
              <Tab
                label={
                  <Text bold={true} sx={{ textTransform: 'none' }} variant="body1">
                    {isSmallMobile ? copy.past : copy.past_transactions}
                  </Text>
                }
              />
            </Tabs>
            {selectedTab === 0 && (
              <RecurrenceList
                onRecurrenceClick={onUpcomingRecurrenceClick}
                recurrences={upcomingRecurrences}
              />
            )}
            {selectedTab === 1 && (
              <RecurrenceList
                onRecurrenceClick={onPaidRecurrenceClick}
                recurrences={pastRecurrences}
              />
            )}
          </Stack>
        )}
      </Grid>

      {isDesktop && (
        <Grid size={{ xs: 'auto' }} sx={{ minWidth: 450 }}>
          <Stack gap={24}>
            <RecurrenceCalendar />
            <RecurrenceList
              onRecurrenceClick={onPaidRecurrenceClick}
              recurrences={selectedDayRecurrences || pastRecurrences}
              title={
                selectedDay
                  ? (buildDynamicCopy(
                      copy.day_transactions,
                      formatDate(selectedDay, DATE_FORMATS_INTL.MONTH_DAY),
                    ) as string)
                  : copy.past_transactions
              }
            />
          </Stack>
        </Grid>
      )}

      <RecurringTransactionDetailsDrawer />
    </Grid>
  )
})
